import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { l } from '@locale/setupI18n';

export enum ValuationSection {
  InterimCashflows = 'InterimCashflows',
  ExitProceeds = 'ExitProceeds',
  InstrumentTotal = 'InstrumentTotal',
}

interface ValuationLabelParams {
  section: ValuationSection;
  instrument: InstrumentDefinitionWithId;
  trancheIndex?: number;
  hasInterims?: boolean;
  interpolationOption?: { escapeValue: boolean };
}

const isMultiTranche = (instrument: InstrumentDefinitionWithId): boolean =>
  (instrument.payoutLogic?.length ?? 0) > 1;

const shouldUseExitProceedsLabel = (
  instrument: InstrumentDefinitionWithId,
  hasInterims?: boolean
): boolean => isMultiTranche(instrument) || Boolean(hasInterims);

export const getPvLabel = ({
  section,
  instrument,
  trancheIndex,
  hasInterims,
  interpolationOption,
}: ValuationLabelParams): string => {
  if (section === ValuationSection.InterimCashflows) {
    return l('_TotalPvOfInterimCashFlows');
  }

  if (section === ValuationSection.InstrumentTotal) {
    return l('_PVOf', {
      Instrument: instrument.instrumentNarrative,
      interpolation: interpolationOption,
    });
  }

  if (isMultiTranche(instrument)) {
    return l('_PvOfTrancheN', { n: trancheIndex, interpolation: interpolationOption });
  }

  if (shouldUseExitProceedsLabel(instrument, hasInterims)) {
    return l('_PvOfExitProceeds');
  }

  return l('_PvOfInstrumentName', {
    instrument: instrument.instrumentNarrative,
    interpolation: interpolationOption,
  });
};

export const getPostDiscountPvLabel = ({
  section,
  instrument,
  trancheIndex,
  hasInterims,
  interpolationOption,
}: ValuationLabelParams): string => {
  if (section === ValuationSection.InterimCashflows) {
    return l('_PostDiscountPVOfInterimCashFlows');
  }

  if (section === ValuationSection.InstrumentTotal) {
    return l('_PostDiscountPvOfInstrumentName', {
      instrument: instrument.instrumentNarrative,
      interpolation: interpolationOption,
    });
  }

  if (isMultiTranche(instrument)) {
    return l('_PostDiscountPvOfTrancheN', { n: trancheIndex });
  }

  if (shouldUseExitProceedsLabel(instrument, hasInterims)) {
    return l('_PostDiscountPvOfExitProceeds');
  }

  return l('_PostDiscountPvOfInstrumentName', {
    instrument: instrument.instrumentNarrative,
    interpolation: interpolationOption,
  });
};

export const getMvLabel = ({
  section,
  instrument,
  trancheIndex,
  hasInterims,
  interpolationOption,
}: ValuationLabelParams): string => {
  if (section === ValuationSection.InterimCashflows) {
    return l('_MvOfInterimCashflows');
  }

  if (section === ValuationSection.InstrumentTotal) {
    return l('_MVOf', {
      Instrument: instrument.instrumentNarrative,
      interpolation: interpolationOption,
    });
  }

  // ExitProceeds
  if (isMultiTranche(instrument)) {
    return l('_MvOfTrancheN', { n: trancheIndex });
  }

  if (shouldUseExitProceedsLabel(instrument, hasInterims)) {
    return l('_MvOfExitProceeds');
  }

  return l('_MvOfInstrumentName', {
    instrument: instrument.instrumentNarrative,
    interpolation: interpolationOption,
  });
};

export const getWeightedAverageMvLabel = ({
  section,
  instrument,
  trancheIndex,
  hasInterims,
  interpolationOption,
}: ValuationLabelParams): string => {
  if (section === ValuationSection.InterimCashflows) {
    return l('_WeightedAverageMvOfInterimCashflows');
  }

  if (section === ValuationSection.InstrumentTotal) {
    return l('_WeightedAverageMvOfInstrument', {
      instrument: instrument.instrumentNarrative,
      interpolation: interpolationOption,
    });
  }

  if (isMultiTranche(instrument)) {
    return l('_WeightedAverageMvOfTrancheN', { n: trancheIndex });
  }

  if (shouldUseExitProceedsLabel(instrument, hasInterims)) {
    return l('_WeightedAverageMvOfExitProceeds');
  }

  return l('_WeightedAverageMvOfInstrument', {
    instrument: instrument.instrumentNarrative,
    interpolation: interpolationOption,
  });
};
